import { ImageUp, Trash2, Upload } from 'lucide-react'
import { useId, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn, formatBytes } from '@/lib/utils'

const MAX_BYTES = 2 * 1024 * 1024
const ACCEPT = 'image/png,image/jpeg,image/svg+xml,image/webp'

/**
 * An image setting, shown on the background it will actually appear against.
 *
 * That is the whole reason this is not a file input with a filename beside it.
 * Both settings this serves — the bot's video feed and the two email marks —
 * are chosen for a background the uploader is not looking at, so the well IS
 * that background: a white mark on white reads as a missing upload here, which
 * is the mistake worth catching before it ships to a recipient.
 *
 * No API, so a file is read to a data URL and held in memory. The upload
 * boundary is this one function when the backend is wired.
 */
export function ImageWell({
  label,
  hint,
  value,
  onChange,
  surface = 'sunken',
  aspect = 'video',
  disabled,
}: {
  label: React.ReactNode
  hint?: React.ReactNode
  value: string | null
  onChange: (dataUrl: string | null) => void
  /** The background the image is composited on, in the product and here. */
  surface?: 'sunken' | 'light' | 'dark'
  aspect?: 'video' | 'wide'
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const id = useId()

  function accept(file: File | undefined) {
    if (!file) return
    if (!ACCEPT.split(',').includes(file.type)) {
      setError('PNG, JPEG, SVG or WebP only.')
      return
    }
    if (file.size > MAX_BYTES) {
      setError(`${formatBytes(file.size)} is over the ${formatBytes(MAX_BYTES)} limit.`)
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      setError(null)
      onChange(String(reader.result))
    }
    reader.onerror = () => setError('That file could not be read.')
    reader.readAsDataURL(file)
  }

  const surfaces = {
    sunken: 'bg-[var(--color-surface-sunken)]',
    light: 'bg-white',
    dark: 'bg-[oklch(0.19_0.008_265)]',
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-[var(--color-fg-muted)]">{label}</span>
        {value ? (
          <span className="flex shrink-0 items-center gap-1">
            <Button
              size="xs"
              variant="ghost"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="size-3" />
              Replace
            </Button>
            <Button
              size="xs"
              variant="ghost"
              disabled={disabled}
              className="hover:text-[var(--color-danger)]"
              aria-label={`Remove ${typeof label === 'string' ? label : 'image'}`}
              onClick={() => {
                setError(null)
                onChange(null)
              }}
            >
              <Trash2 className="size-3" />
            </Button>
          </span>
        ) : null}
      </div>

      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          accept(event.dataTransfer.files[0])
        }}
        className={cn(
          'grid w-full place-items-center overflow-hidden rounded-[var(--radius-md)] border p-3',
          'outline-none transition-colors duration-150',
          'focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35',
          aspect === 'video' ? 'aspect-video' : 'h-24',
          surfaces[surface],
          dragging
            ? 'border-[var(--color-primary)] ring-2 ring-[var(--color-primary)]/25'
            : value
              ? 'border-[var(--color-border)]'
              : 'border-dashed border-[var(--color-border-strong)] hover:border-[var(--color-fg-subtle)]',
          disabled && 'pointer-events-none opacity-50'
        )}
      >
        {value ? (
          <img src={value} alt="" className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="flex flex-col items-center gap-1">
            <ImageUp
              className={cn(
                'size-4',
                surface === 'dark' ? 'text-white/40' : 'text-[var(--color-fg-subtle)]'
              )}
            />
            <span
              className={cn(
                'text-2xs',
                surface === 'dark' ? 'text-white/50' : 'text-[var(--color-fg-subtle)]'
              )}
            >
              Drop an image, or click to choose
            </span>
          </span>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(event) => {
          accept(event.target.files?.[0])
          // Same file twice in a row still fires a change.
          event.target.value = ''
        }}
      />

      {error ? (
        <p className="text-xs text-[var(--color-danger)]" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-[var(--color-fg-subtle)]">{hint}</p>
      ) : null}
    </div>
  )
}
